export const SERVICES = [
  {
    slug: "day-to-day-hr-advice",
    title: "Day-to-Day HR Advice",
    shortTitle: "Day-to-Day Advice",
    icon: "MessageSquare",
    tagline: "Answers in hours, not invoice cycles.",
    metaTitle: "Day-to-Day HR Advice for UK Small Businesses | Electra HR",
    metaDescription: "Fast, plain-English HR advice for UK founders and SMEs. Answers in hours, not invoice cycles. Pay-as-you-go, no retainers. Same-day quotes.",
    problem: "Most people problems don't need a retainer. They need a straight answer from someone who has seen it before, while the situation is still small enough to fix.",
    included: [
      "Same-day answers on disciplinaries, absence, performance and conduct",
      "Phone, email or video, whichever gets you the answer fastest",
      "Scripts, letters and meeting notes drafted for you, in plain English",
      "Risk flags raised early, before they become claims",
      "Fixed-fee or pay-as-you-go, never a retainer you don't use",
    ],
    retainerGap: [
      "You pay monthly whether you use it or not",
      "Call handlers read from scripts, not experience",
      "'We'll come back to you' quietly means next week",
      "Advice so cautious it's unusable",
    ],
    steps: [
      { title: "Tell us what's happening", text: "A few lines on the enquiry form, or a call with an adviser if you prefer. No intake forms designed to waste your time." },
      { title: "Get a straight answer", text: "What you can do, what you can't, and the safest route through, the same day." },
      { title: "We draft what you need", text: "Letters, scripts, meeting notes, ready to use, in your voice." },
      { title: "We stay on it", text: "If it escalates, we escalate with you, including a route into Bonsai Law if it turns legal." },
    ],
  },
  {
    slug: "grievances-disciplinaries",
    title: "Grievances & Disciplinaries",
    shortTitle: "Grievances",
    icon: "Scale",
    tagline: "Handle it fairly. Handle it fast. Handle it once.",
    metaTitle: "Grievance & Disciplinary Support for UK SMEs | Electra HR",
    metaDescription: "Handle grievances and disciplinaries fairly, fast and once. Fixed-fee support for UK small businesses. Solicitor-founded, tribunal-aware.",
    problem: "A grievance handled badly becomes a tribunal claim. A disciplinary handled slowly poisons the team. You need a process that is fair, fast and defensible, not a 40-page policy nobody follows.",
    included: [
      "Investigation planning and independent investigation support",
      "Hearing scripts and question plans for managers",
      "Outcome letters and appeal handling, drafted for you",
      "Suspension and interim-measure decisions, stress-tested",
      "Escalation triage: when a matter needs Bonsai Law's SRA-regulated lawyers, we tell you immediately",
    ],
    retainerGap: [
      "Template letters that ignore your actual facts",
      "Processes so slow the team loses faith",
      "Nobody tells you when the risk has turned legal",
      "You do the meeting alone, script unseen",
    ],
    steps: [
      { title: "Triage within hours", text: "We assess severity, legal risk and the right process, before anything is said that can't be unsaid." },
      { title: "Plan the process", text: "Who investigates, who hears it, what gets said, mapped out and documented." },
      { title: "Run it with you", text: "Scripts, letters and on-call support through every meeting." },
      { title: "Close it properly", text: "Outcomes, appeals and follow-through, so the issue stays closed." },
    ],
  },
  {
    slug: "contracts-handbooks",
    title: "Contracts & Handbooks",
    shortTitle: "Contracts",
    icon: "FileText",
    tagline: "Paperwork that protects you, and people actually read.",
    metaTitle: "Employment Contracts & Staff Handbooks for UK SMEs | Electra HR",
    metaDescription: "Bespoke employment contracts and staff handbooks for UK small businesses. Compliant, plain-English, tailored to how your business actually runs.",
    problem: "Template contracts copied from the internet contradict your handbook, miss day-one requirements, and fall apart the first time you rely on them. Your paperwork should match how your business actually runs.",
    included: [
      "Employment contracts drafted or reviewed, day-one compliant",
      "Staff handbooks in plain English your team will actually read",
      "Policies matched to your real practice, not aspirational fiction",
      "Probation, notice, restrictive covenant and flexibility clauses done properly",
      "Annual update check-ins so documents never go stale",
    ],
    retainerGap: [
      "One-size templates that contradict each other",
      "Legalese nobody reads until it's too late",
      "Updates you pay for but never receive",
      "Documents that crumble under scrutiny",
    ],
    steps: [
      { title: "We read what you have", text: "A blunt audit of your current contracts and handbook. What protects you and what doesn't." },
      { title: "We learn how you work", text: "Hours, remote patterns, overtime reality, the documents must match the practice." },
      { title: "We draft in plain English", text: "Compliant, tailored, readable. No filler clauses copied from a law firm's attic." },
      { title: "We roll it out with you", text: "Including the consultation and communication side, where changes need it." },
    ],
  },
  {
    slug: "hr-health-checks",
    title: "HR Health Checks",
    shortTitle: "Health Checks",
    icon: "HeartPulse",
    tagline: "Find the risks before they find you.",
    metaTitle: "HR Health Check & Audit for UK Small Businesses | Electra HR",
    metaDescription: "Find HR risks before they find you. Full audit of contracts, policies and processes for UK SMEs. Fixed fee, written report, prioritised fixes.",
    problem: "Most SMEs carry HR risk they can't see: stale contracts, missing policies, managers improvising. A health check finds it all, while fixing it is still cheap.",
    included: [
      "Full review of contracts, handbook, policies and practice",
      "Right-to-work, holiday pay and working-time compliance check",
      "A plain-English report: what's fine, what's risky, what's urgent",
      "A prioritised fix list with fixed prices per item",
      "A follow-up call to walk through it and answer everything",
    ],
    retainerGap: [
      "'Audits' that exist to sell you a retainer",
      "Hundred-page reports nobody acts on",
      "Fear-based findings with no proportion",
      "No fixed price to fix what's found",
    ],
    steps: [
      { title: "Send us your paperwork", text: "Contracts, handbook, policies, whatever exists. We work with what there is." },
      { title: "We interrogate it", text: "Against current employment law and, just as importantly, against your actual practice." },
      { title: "You get the blunt version", text: "A report that ranks every finding by real-world risk, not by what sounds scary." },
      { title: "Fix what matters", text: "Pick items off the fix list at fixed prices. No retainer required, ever." },
    ],
  },
  {
    slug: "training-and-change",
    title: "Training & Change",
    shortTitle: "Training & Change",
    icon: "GraduationCap",
    tagline: "Managers who can handle people. Change that doesn't break them.",
    metaTitle: "Manager Training & Change Management for UK SMEs | Electra HR",
    metaDescription: "Practical people-management training for line managers, and change support for restructures, TUPE and reorganisations. UK small businesses.",
    problem: "Most people problems land on managers who were never trained for them. And most restructures fail on communication, not spreadsheets. Both are fixable with practical, honest support.",
    included: [
      "Manager training: hard conversations, absence, performance, conduct",
      "Restructure and change planning: process, timelines, consultation scripts",
      "Redundancy process support, done fairly and defensibly",
      "Team communications drafted in your voice",
      "Legal escalation into Bonsai Law the moment a change programme turns contentious",
    ],
    retainerGap: [
      "Generic e-learning nobody remembers",
      "Change plans designed for corporates, not 20-person teams",
      "Consultation handled as a box-tick",
      "Managers left alone in the hardest meetings",
    ],
    steps: [
      { title: "Diagnose the real gap", text: "We find where managers or the change plan will actually break, not where a textbook says." },
      { title: "Train or plan, practically", text: "Live, scenario-based sessions and change plans built for your team's size." },
      { title: "Support the hard moments", text: "We're on call for the meetings and announcements that matter." },
      { title: "Review what landed", text: "Change only counts if it sticks. We check it did." },
    ],
  },
  {
    slug: "startup-packages",
    title: "Startup HR Packages",
    shortTitle: "Startup Packages",
    icon: "Rocket",
    tagline: "HR foundations for teams of 5 to 50, built in weeks.",
    metaTitle: "Startup HR Packages, HR for Teams of 5 to 50 | Electra HR",
    metaDescription: "Complete HR foundations for UK startups and scaleups, contracts, handbook, policies, onboarding. Delivered in weeks, fixed fee, no retainer.",
    problem: "Startups don't need an HR department. They need the foundations done properly, once: contracts, a handbook people read, and managers who know the basics. Everything else can wait.",
    included: [
      "Day-one compliant contracts for every role type",
      "A short, sharp handbook covering what actually matters",
      "Onboarding and probation frameworks that get used",
      "Founder coaching on the people calls you'll face first",
      "A direct line to us for year one, pay-as-you-go, no retainer",
    ],
    retainerGap: [
      "Retainers sized for corporates, sold to startups",
      "Foundations stretched over six billable months",
      "Template packs with zero tailoring",
      "Nobody picks up when the first real problem lands",
    ],
    steps: [
      { title: "One quick scoping", text: "Team size, roles, plans, by form or a short call with an adviser. You get a fixed fee in writing." },
      { title: "Foundations built in weeks", text: "Contracts, handbook, frameworks, drafted, tailored, delivered." },
      { title: "Founders briefed", text: "A working session so you know exactly what you have and how to use it." },
      { title: "On call as you grow", text: "When hiring gets messy or someone quits loudly, we're one message away." },
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
